import { useState } from 'react';
import { X, Settings, Palette, Type, Moon, Sun, Monitor } from 'lucide-react';
import type { AppSettings } from '../../utils/appSettings';
import { loadAppSettings, setAndSyncAppSettings, applyAppSettings, DEFAULT_APP_SETTINGS } from '../../utils/appSettings';

interface Props {
  onClose: () => void;
}

export default function AppSettingsModal({ onClose }: Props) {
  const [settings, setSettings] = useState<AppSettings>(loadAppSettings());

  function update(patch: Partial<AppSettings>) {
    const next = { ...settings, ...patch };
    setSettings(next);
    applyAppSettings(next);
    // Sprint 8: PUT no servidor + cache local. Best-effort (não trava UI).
    void setAndSyncAppSettings(next);
  }

  const ACCENT_PRESETS = [
    { color: '#6366f1', name: 'Índigo' },
    { color: '#8b5cf6', name: 'Violeta' },
    { color: '#ec4899', name: 'Rosa' },
    { color: '#ef4444', name: 'Vermelho' },
    { color: '#f59e0b', name: 'Âmbar' },
    { color: '#10b981', name: 'Esmeralda' },
    { color: '#06b6d4', name: 'Ciano' },
    { color: '#3b82f6', name: 'Azul' },
    { color: '#1d4ed8', name: 'Azul Escuro' },
    { color: '#0f172a', name: 'Grafite' },
  ];

  const FONT_OPTIONS = [
    { value: 'Inter, system-ui, sans-serif', label: 'Inter (padrão)' },
    { value: 'system-ui, sans-serif', label: 'Sistema' },
    { value: '"Roboto", sans-serif', label: 'Roboto' },
    { value: '"Poppins", sans-serif', label: 'Poppins' },
    { value: '"Nunito", sans-serif', label: 'Nunito' },
    { value: '"DM Sans", sans-serif', label: 'DM Sans' },
    { value: 'Georgia, serif', label: 'Georgia (serif)' },
    { value: '"JetBrains Mono", monospace', label: 'Monospace' },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)' }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
        style={{ background: 'var(--app-surface)', color: 'var(--app-text)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--app-border)' }}>
          <div className="flex items-center gap-2.5">
            <Settings size={18} style={{ color: 'var(--app-accent)' }} />
            <h2 className="text-base font-bold">Personalizar Sistema</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors" style={{ color: 'var(--app-text-muted)' }}>
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Logo */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-muted)' }}>
              <Monitor size={13} /> Logo do Sistema
            </h3>
            <input
              type="text"
              value={settings.logo}
              onChange={e => update({ logo: e.target.value })}
              placeholder="URL da logo (https://...)"
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none transition-colors"
              style={{ borderColor: 'var(--app-border)', background: 'var(--app-surface-2)', color: 'var(--app-text)' }}
            />
            {settings.logo && (
              <div className="mt-2 h-12 rounded-lg flex items-center px-3 border" style={{ borderColor: 'var(--app-border)', background: 'var(--app-surface-2)' }}>
                <img src={settings.logo} alt="Logo" className="max-h-8 object-contain" />
              </div>
            )}
          </section>

          {/* Modo */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-muted)' }}>
              <Moon size={13} /> Modo
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => update({ darkMode: false })}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-all ${!settings.darkMode ? 'border-[var(--app-accent)] text-white' : 'hover:border-gray-300'}`}
                style={!settings.darkMode ? { background: 'var(--app-accent)', borderColor: 'var(--app-accent)' } : { borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
              >
                <Sun size={15} /> Claro
              </button>
              <button
                onClick={() => update({ darkMode: true })}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-all ${settings.darkMode ? 'text-white' : 'hover:border-gray-300'}`}
                style={settings.darkMode ? { background: 'var(--app-accent)', borderColor: 'var(--app-accent)', color: '#fff' } : { borderColor: 'var(--app-border)', color: 'var(--app-text)' }}
              >
                <Moon size={15} /> Escuro
              </button>
            </div>
          </section>

          {/* Cor de destaque */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-muted)' }}>
              <Palette size={13} /> Cor Principal
            </h3>
            <div className="flex flex-wrap gap-2 mb-3">
              {ACCENT_PRESETS.map(p => (
                <button
                  key={p.color}
                  title={p.name}
                  onClick={() => update({ accentColor: p.color })}
                  className="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110"
                  style={{
                    background: p.color,
                    borderColor: settings.accentColor === p.color ? 'var(--app-text)' : 'transparent',
                    boxShadow: settings.accentColor === p.color ? '0 0 0 2px var(--app-surface), 0 0 0 4px var(--app-text)' : 'none',
                  }}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={settings.accentColor}
                onChange={e => update({ accentColor: e.target.value })}
                className="w-10 h-9 rounded border cursor-pointer p-0.5"
                style={{ borderColor: 'var(--app-border)' }}
              />
              <input
                type="text"
                value={settings.accentColor}
                onChange={e => { if (/^#[0-9a-fA-F]{0,6}$/.test(e.target.value)) update({ accentColor: e.target.value }); }}
                className="flex-1 border rounded-lg px-3 py-2 text-sm font-mono focus:outline-none"
                style={{ borderColor: 'var(--app-border)', background: 'var(--app-surface-2)', color: 'var(--app-text)' }}
              />
            </div>
          </section>

          {/* Fonte */}
          <section>
            <h3 className="text-xs font-bold uppercase tracking-wider mb-3 flex items-center gap-2" style={{ color: 'var(--app-text-muted)' }}>
              <Type size={13} /> Fonte
            </h3>
            <select
              value={settings.fontFamily}
              onChange={e => update({ fontFamily: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none"
              style={{ borderColor: 'var(--app-border)', background: 'var(--app-surface-2)', color: 'var(--app-text)' }}
            >
              {FONT_OPTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
            <p className="text-xs mt-1.5" style={{ color: 'var(--app-text-muted)', fontFamily: settings.fontFamily }}>
              Preview da fonte selecionada — FormCraft
            </p>
          </section>

          {/* Reset */}
          <div className="border-t pt-4" style={{ borderColor: 'var(--app-border)' }}>
            <button
              onClick={() => { update(DEFAULT_APP_SETTINGS); }}
              className="text-sm hover:opacity-80 transition-opacity"
              style={{ color: 'var(--app-text-muted)' }}
            >
              Restaurar padrões
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
